/**
 * Split Banner Web Component
 * --------------------------
 * A reusable two-column layout component with content and image sections.
 * On desktop, content appears on the left and image on the right.
 * On tablet/mobile, the layout stacks with image on top and content below.
 *
 * Attributes:
 * - reversed: Flip content/image sides on desktop (image left, content right)
 * - gap: Custom gap between columns (CSS length, default 64px)
 * - mobile-gap: Custom gap when stacked (CSS length, default 24px)
 * - stack-at: Breakpoint to stack ("tablet" default = 1024px, or "mobile" = 768px)
 * - content-valign: Vertical alignment ("start", "center", "end")
 *
 * Slots:
 * - header: Main heading area
 * - body: Middle content (bullet list, description, etc.)
 * - footer: Bottom content (CTA button, links, etc.)
 * - image: Visual/image area
 *
 * Example Usage:
 *  <split-banner>
 *    <h2 slot="header">Heading</h2>
 *    <div slot="body"><ul><li>Point 1</li></ul></div>
 *    <a slot="footer" href="#">CTA</a>
 *    <img slot="image" src="hero.png" alt="Hero" />
 *  </split-banner>
 *
 *  <split-banner reversed stack-at="mobile">
 *    ...
 *  </split-banner>
 */

const SplitBanner = (function () {
  // --- Define component template (structure + scoped styles) ---
  let comptemplate = document.createElement("template");
  comptemplate.innerHTML = `
    <style>
      :host {
        display: block;
      }

      .split-banner {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: var(--split-banner-gap, 4rem);
        align-items: var(--split-banner-valign, center);
      }

      .split-banner__content {
        display: flex;
        flex-direction: column;
        order: 0;
        min-width: 0;
      }

      .split-banner__image {
        order: 1;
        min-width: 0;
      }

      /* Reversed: swap column order on desktop */
      :host([reversed]) .split-banner__content {
        order: 1;
      }

      :host([reversed]) .split-banner__image {
        order: 0;
      }

      /* --- Default stacking: tablet breakpoint (1024px) --- */
      @media (max-width: 1023px) {
        :host(:not([stack-at="mobile"])) .split-banner {
          grid-template-columns: 1fr;
          gap: var(--split-banner-mobile-gap, 1.5rem);
        }

        :host(:not([stack-at="mobile"])) .split-banner__image {
          order: -1;
        }

        :host(:not([stack-at="mobile"])) .split-banner__content {
          order: 0;
        }

        :host(:not([stack-at="mobile"])[reversed]) .split-banner__content {
          order: 0;
        }

        :host(:not([stack-at="mobile"])[reversed]) .split-banner__image {
          order: -1;
        }
      }

      /* --- Alternative stacking: mobile breakpoint (768px) --- */
      @media (max-width: 767px) {
        :host([stack-at="mobile"]) .split-banner {
          grid-template-columns: 1fr;
          gap: var(--split-banner-mobile-gap, 1.5rem);
        }

        :host([stack-at="mobile"]) .split-banner__image {
          order: -1;
        }

        :host([stack-at="mobile"]) .split-banner__content {
          order: 0;
        }

        :host([stack-at="mobile"][reversed]) .split-banner__content {
          order: 0;
        }

        :host([stack-at="mobile"][reversed]) .split-banner__image {
          order: -1;
        }
      }
    </style>

    <div class="split-banner">
      <div class="split-banner__content">
        <slot name="header"></slot>
        <slot name="body"></slot>
        <slot name="footer"></slot>
      </div>
      <div class="split-banner__image">
        <slot name="image"></slot>
      </div>
    </div>
  `;

  /**
   * SplitBanner custom element definition.
   */
  class SplitBanner extends HTMLElement {
    static get observedAttributes() {
      return ["gap", "mobile-gap", "content-valign"];
    }

    constructor() {
      super();
    }

    /**
     * Lifecycle: called when the element is inserted into the DOM.
     * Attaches Shadow DOM and injects the template.
     */
    connectedCallback() {
      this.root = this.attachShadow({ mode: "open" });
      this.root.appendChild(comptemplate.content.cloneNode(true));
      this.updateCustomProperties();
    }

    /**
     * Called when an observed attribute changes.
     * Updates CSS custom properties to reflect new values.
     */
    attributeChangedCallback(name, oldValue, newValue) {
      if (oldValue === newValue) return;
      this.updateCustomProperties();
    }

    /**
     * Maps HTML attributes to CSS custom properties on the host element.
     */
    updateCustomProperties() {
      const gap = this.getAttribute("gap");
      const mobileGap = this.getAttribute("mobile-gap");
      const valign = this.getAttribute("content-valign");

      if (gap) {
        this.style.setProperty("--split-banner-gap", gap);
      }
      if (mobileGap) {
        this.style.setProperty("--split-banner-mobile-gap", mobileGap);
      }
      if (valign) {
        this.style.setProperty("--split-banner-valign", valign);
      }
    }
  }

  // --- Register the custom element ---
  customElements.define("split-banner", SplitBanner);

  return SplitBanner;
})();

export { SplitBanner };
